import React, { useState } from 'react';
import AdminList, { ArticleCountCellRenderer, NameCellRenderer, ReadOnlyLabelCellRenderer } from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { createStyles, makeStyles } from '@material-ui/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { GET_CHANNELS_PUBLISHERS_DASHBOARD } from '../../../gql/channels';
import { sortArray } from '../../../utils/sorting.utils';
import { ExtendedTheme, Loader, Link } from 'neon-design-system';
import { convertToDate, formatDisplayDate } from '../../../utils/date.utils';
import PremiumChannelFilter from '../../ui/admin/PremiumChannelFilter';
import { PremiumChannelKind, PremiumChannelType, ResearchRole } from '../../../shared/global-enums';
import { useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { config } from '../../../config';
import { formatPlural } from '../../../utils/string.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';
import { useUser } from '../../providers/UserProvider';
import { Channel, ChannelState, ChannelTypes } from '../../../shared/types';
import { isChannelAdmin } from '../../../utils/channel.utils';
import { ChannelLabel } from '../../ui/ChannelLabel';

const title = 'Channels';
const name = 'premium-channels';

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	gridHeader: {
		fontWeight: 'bold'
	},
	highlighted: {
		color: theme.palette.secondary.main
	}
}));

const PremiumChannelsList = () => {
	const { hasScope } = useUser();
	const isReadOnly = ![ResearchRole.Administrator, ResearchRole.Editor].some(hasScope);
	const isAdmin = [ResearchRole.Administrator].some(hasScope);
	const { accessToken } = useAuth0Data();
	const neonUserId = jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];
	const [premiumChannelKind, setPremiumChannelKind] = useState(PremiumChannelKind.All);
	const [channelType, setChannelType] = useState(PremiumChannelType.All);
	const { loading, error, data } = useQuery(GET_CHANNELS_PUBLISHERS_DASHBOARD, { fetchPolicy: 'network-only' });
	const classes = useStyles();
	const articleFilter = useArticleFilter();

	const columnDefs: ColDef[] = [
		{
			colId: 'channel-name-col',
			headerName: 'Channel name',
			headerClass: classes.gridHeader,
			field: 'name',
			cellRendererFramework: (props: any) => isAdmin || isChannelAdmin(props.data.adminUsers, neonUserId) ?
				<NameCellRenderer {...props} apiName={name} /> :
				<ReadOnlyLabelCellRenderer {...props} />,
			width: 260,
			menuTabs: []
		},
		{
			colId: 'channel-owner-col',
			headerName: 'Channel Owner',
			headerClass: classes.gridHeader,
			field: 'owner.name',
			width: 260,
			menuTabs: [],
			cellRendererFramework: (props: any) => (isAdmin || isChannelAdmin(props.data.adminUsers, neonUserId)) && props.data.ownerType === 'desk' ?
				<Link to={`${baseUrls.publisher.main}/desks/edit/${props.data.owner._id}`}>{props.value}</Link> :
				<ReadOnlyLabelCellRenderer {...props} />
		},
		{
			headerName: 'Channel type',
			headerClass: classes.gridHeader,
			field: 'type',
			width: 200,
			menuTabs: [],
			cellRendererFramework: (props: any) => <ChannelLabel type={props.data.type} />
		},
		{
			colId: 'channel-articles-col',
			headerName: 'Articles',
			headerClass: classes.gridHeader,
			field: 'articleCount',
			cellRendererFramework: (props: ICellRendererParams) =>
				<ArticleCountCellRenderer
					value={props.data.state === ChannelState.Archived ? props.data.archivedArticleCount : props.value}
					filter={articleFilter}
					context={props.context}
					filterData={{
						_id: props.data._id,
						name: props.data.name,
						type: props.data.state === ChannelState.Archived ? 'archived-channel' : 'channel'
					}} />,
			width: 200,
			menuTabs: []
		},
		{
			colId: 'channel-subscribers-col',
			headerName: 'Subscribers',
			headerClass: classes.gridHeader,
			field: 'name',
			cellRendererFramework: (props: any) =>
				(isAdmin || isChannelAdmin(props.data.adminUsers, neonUserId)) && props.data.state === ChannelState.Active
					? <Link to={`${baseUrls.publisher.premiumChannels.subscribersStatsMain}/${props.data._id}`}>
						{formatPlural(props.data.channelActiveUsersCount, 'user')}
					</Link>
					: formatPlural(props.data.channelActiveUsersCount, 'user'),
			width: 200,
			menuTabs: []
		},
		{
			colId: 'channel-created-col',
			headerName: 'Created',
			headerClass: classes.gridHeader,
			field: 'createdAt',
			cellRendererFramework: (props: any) => <>{
				formatDisplayDate(convertToDate(props.value), true)
			}</>,
			width: 260,
			menuTabs: []
		}
	];

	const channels = (data?.allChannels as Channel[]) ?? [];

	const kindFilteredChannels = premiumChannelKind === PremiumChannelKind.All
		? channels.filter(item => item.state === ChannelState.Active)
		: premiumChannelKind === PremiumChannelKind['My Channels']
			? channels.filter(item => ((item.owner._id === neonUserId) && (item.state === ChannelState.Active)))
			: channels.filter(item => item.state === ChannelState.Archived);

	const typeFilteredChannels = channelType === PremiumChannelType.All
		? kindFilteredChannels
		: kindFilteredChannels.filter(ch => channelType === PremiumChannelType.Premium
			? ch.type === ChannelTypes.premium
			: ch.type === ChannelTypes.freeClosed || ch.type === ChannelTypes.freeOpen)

	const sortedChannels = sortArray(typeFilteredChannels, 'name', true);

	if (loading) return <Loader />;

	return (
		<AdminList
			title={title}
			name={name}
			items={sortedChannels}
			loading={loading}
			error={error}
			columns={columnDefs}
			showAdd={!isReadOnly}
			addButtonLabel="New channel"
			entityForCaptionText="channels"
			toolbar={
				<PremiumChannelFilter
					premiumChannelKind={premiumChannelKind}
					setPremiumChannelKind={setPremiumChannelKind}
					channelType={channelType}
					setChannelType={setChannelType}
				/>
			}
		/>
	);
};

export default PremiumChannelsList;
